import _ from 'lodash';

/**
 * @private
 *
 * @method
 *
 * @description Checks if the form title or description has changed.
 *
 * @param {ReduxState} state
 *
 * @returns {boolean} true if form title or description have changed.
 */
const checkFormDetails = (state) => {
  const {
    JSONSchema: updatedJSONSchema
  } = state.form;
  const {
    JSONSchema: pristineJSONSchema
  } = state.pristineForm;

  const formDetailsChanged = updatedJSONSchema.title !== pristineJSONSchema.title
  || updatedJSONSchema.description !== pristineJSONSchema.description;

  return formDetailsChanged;
};

/**
 * @private
 *
 * @method
 *
 * @description Checks if the task assignees or deadline has changed.
 *
 * @param {ReduxState} state
 *
 * @returns {boolean} true if the task assignees or deadline has changed.
 */
const checkTaskCustomization = (state) => {
  const {
    assignTasksTo: updatedAssignTasksTo,
    tasksDeadline: updatedTasksDeadline,
    taskSummary: updatedTaskSummary,
    headerImage: updatedHeaderImage,
    confirmationText: updatedConfirmationText,
    buttonLabel: updatedButtonLabel,
    buttonColor: updatedButtonColor
  } = state.form;

  const {
    assignTasksTo: pristineAssignTasksTo,
    tasksDeadline: pristineTasksDeadline,
    taskSummary: pristineTaskSummary,
    headerImage: pristineHeaderImage,
    confirmationText: pristineConfirmationText,
    buttonLabel: pristineButtonLabel,
    buttonColor: pristineButtonColor
  } = state.pristineForm;

  const deadlineChanged = updatedTasksDeadline !== pristineTasksDeadline;
  const taskSummaryChanged = updatedTaskSummary !== pristineTaskSummary;
  const headerImageChanged = updatedHeaderImage !== pristineHeaderImage;
  const confirmationTextChanged = updatedConfirmationText !== pristineConfirmationText;
  const buttonLabelChanged = updatedButtonLabel !== pristineButtonLabel;
  const buttonColorChanged = updatedButtonColor !== pristineButtonColor;
  
  if (taskSummaryChanged || deadlineChanged || headerImageChanged || confirmationTextChanged || buttonLabelChanged || buttonColorChanged
      || updatedAssignTasksTo.length !== pristineAssignTasksTo.length) {
    return true;
  }
  
  const updatedIds = updatedAssignTasksTo.map(user => (user.id));
  let differentAssignees = false;

  let i;
  for (i = 0; i < updatedIds.length; i += 1) {
    if (updatedIds.indexOf(pristineAssignTasksTo[i]) === -1) {
      differentAssignees = true;
      break;
    }
  }

  return differentAssignees;
};

/**
 * @private
 *
 * @method
 *
 * @description Checks if an element has been removed or added.
 *
 * @param {ReduxState} state
 *
 * @returns {boolean} true if there are new or less elements.
 */
const checkElementsRemoveOrCreate = (state) => {
  const {
    properties: updatedProperties
  } = state.form.JSONSchema;

  const {
    properties: pristineProperties
  } = state.pristineForm.JSONSchema;

  const updatedPropertiesIds = _.map(updatedProperties, 'id');
  const pristinePropertiesIds = _.map(pristineProperties, 'id');

  const elementsQuantityModified = updatedPropertiesIds.length !== pristinePropertiesIds.length
    || _.difference(
      updatedPropertiesIds,
      pristinePropertiesIds
    ).length > 0;

  return elementsQuantityModified;
};

/**
 * @private
 *
 * @method
 *
 * @description Checks if the order (which one is first, last, etc) of the elements has changed.
 *
 * @param {ReduxState} state
 *
 * @returns {boolean} true if the elements order has changed.
 */
const checkElementsOrder = (state) => {
  const {
    UISchema: updatedUISchema
  } = state.form;
  const {
    UISchema: pristineUISchema
  } = state.pristineForm;

  const orderModified = !_.isEqual(
    updatedUISchema['ui:order'],
    pristineUISchema['ui:order']
  );

  return orderModified;
};

/**
 * @private
 *
 * @method
 *
 * @description Checks if which fields are maked as required have changed.
 *
 * @param {ReduxState} state
 *
 * @returns {boolean} true if the required fields are different.
 */
const checkElementsRequired = (state) => {
  const {
    required: updatedRequired
  } = state.form.JSONSchema;
  const {
    required: pristineRequired
  } = state.pristineForm.JSONSchema;

  const requiredModified = updatedRequired.length !== pristineRequired.length
    || _.difference(
      updatedRequired,
      pristineRequired
    ).length > 0;

  return requiredModified;
};

/**
 * @private
 *
 * @method
 *
 * @description
 * Checks if there have been changes to any properties directly controlled by an element,
 * the possible properties are: title, description, default, type, and format.
 *
 * @param {ReduxState} state
 *
 * @returns {boolean} true if any of the properties presents changes.
 */
const checkElementDetails = (state) => {
  const {
    selectedElement: updatedElement
  } = state;
  const {
    properties: pristineElements
  } = state.pristineForm.JSONSchema;

  if (_.isNull(updatedElement)) {
    return false;
  }

  const pristineElementDetails = _.find(pristineElements, { id: updatedElement.id });

  if (_.isUndefined(pristineElementDetails)) {
    return false;
  }

  // The required property at element level is only for rendering so we need to ignore it
  // the difference in required fields is controlled by an array
  // which has it's own checker (checkElementsRequired)
  const updatedElementDetails = _.omit(updatedElement, 'required');

  const elementDetailsModidified = !_.isEqual(updatedElementDetails, pristineElementDetails);

  return elementDetailsModidified;
};

/**
 * @private
 *
 * @constant
 *
 * @description
 * Selector object containing all different checkers to be called secuentially
 * by the check method.
 */
const CHECKERS = {
  checkFormDetails,
  checkTaskCustomization,
  checkElementsRemoveOrCreate,
  checkElementsOrder,
  checkElementsRequired,
  checkElementDetails
};

/**
 * @public
 *
 * @method
 *
 * @description
 * Compares the current state of the form against the pristine version of it,
 * a pristine version of the form would be, the state when the editor first loaded,
 * or the state of the form since the last time it was saved durin an edition session
 *
 * @param {ReduxState} state
 *
 * @returns {boolean}
 * true if anything in the form is different from it's pristine state. And since some
 * changes are reversible i.e: adding one letter to the form title, and erasing it
 * this method will return false on if the form returns to it's pristine state at any time.
 */
const check = (state) => {
  let isModified = false;

  const CHECKERS_KEYS = _.keys(CHECKERS);

  // Using a regular for loop to be able to break after first positive
  for (let i = 0, size = CHECKERS_KEYS.length; i < size; i += 1) {
    const checkerKey = CHECKERS_KEYS[i];
    const checker = CHECKERS[checkerKey];

    if (checker(state)) {
      isModified = true;
      break;
    }
  }

  return isModified;
};

export default {
  check
};
